import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ParticlesBackground } from "@/components/effects";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";

export function FullScreenCTA() {
  const { t } = useTranslation('home');
  const { language } = useLanguage();
  
  const ArrowIcon = language === 'he' ? ArrowLeft : ArrowRight;
  const whatsappMessage = language === 'he' 
    ? "שלום,%20אשמח%20לקבל%20פרטים%20נוספים"
    : "Hello,%20I'd%20like%20more%20information";

  return (
    <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-primary" />
      
      {/* Mesh Gradient */}
      <div 
        className="absolute inset-0 opacity-50"
        style={{
          background: `
            radial-gradient(ellipse at 30% 40%, hsl(38 92% 50% / 0.2) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 60%, hsl(222 47% 30% / 0.15) 0%, transparent 50%)
          `
        }}
      />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 pattern-grid opacity-20" />
      
      {/* Particles */}
      <ParticlesBackground variant="hero" className="opacity-50" />

      {/* Animated Orbs */}
      <motion.div 
        className="absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full bg-accent/10 blur-[100px]"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 6, repeat: Infinity }}
      />

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 relative z-10 text-center py-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Badge */}
          <motion.div 
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 backdrop-blur-sm mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <span className="text-primary-foreground/80 text-sm font-medium">{t('cta.badge')}</span>
          </motion.div>

          {/* Headline */}
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 leading-tight">
            {t('cta.title')}
            <br />
            <span className="text-gradient-accent">{t('cta.titleAccent')}</span>
          </h2>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-primary-foreground/70 mb-12 max-w-2xl mx-auto leading-relaxed">
            {t('cta.subtitle')}
            <br />
            {t('cta.subtitleLine2')}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link to="/contact">
                <Button variant="hero" size="xl" className="text-lg group w-full sm:w-auto">
                  <Phone className="w-5 h-5" />
                  {t('cta.primaryCta')}
                  <motion.span
                    animate={{ x: language === 'he' ? [0, -5, 0] : [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowIcon className="w-5 h-5" />
                  </motion.span>
                </Button>
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <a
                href={`https://wa.me/972528300088?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="whatsapp" size="xl" className="text-lg w-full sm:w-auto">
                  <MessageCircle className="w-5 h-5" />
                  {t('cta.secondaryCta')}
                </Button>
              </a>
            </motion.div>
          </div>

          {/* Trust Text */}
          <p className="text-primary-foreground/50 text-sm">
            {t('cta.trust')}
          </p>
        </motion.div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path 
            d="M0 100L60 90C120 80 240 60 360 50C480 40 600 40 720 45C840 50 960 60 1080 65C1200 70 1320 70 1380 70L1440 70V100H0Z" 
            fill="hsl(var(--background))"
          />
        </svg>
      </div>
    </section>
  );
}
